/**
 * pcConsole Compare 折线图图例布局实现
 * 两个指标 并且 两个及以上数量游戏时，图例两行展示，每个指标一行 超过翻页查看
 * 其他情况 统一一行展示，超过翻页查看
 *
 * 配色统一是 同一个游戏色系相同，根据指标顺序 颜色由深到浅
 */

import { useRef, useState, useEffect } from 'react';
import { useThrottleFn } from 'ahooks';
import { calcLegendTopOfMultiLegendMode } from '../utils';
import {
  ECHART_COLORS,
  FLATTEN_ECHART_COLORS,
  ECHART_BOTTOM,
  ECHART_LEGEND_HEIGHT,
  ECHART_LEGEND_LINE_SPACING,
  ECHART_LEGEND_HORIZONTAL_BOUNDARY_WIDTH,
  ECHART_LEGEND_TOP,
  ECHART_NODE_HEIGHT,
} from '../../../constants/echart';
import type { ILegendComponentOption } from '@/pages/intelligence/types/echart';
import type { IDBIBaseLineChartInstance } from '../../ECharts/BaseLineChart';

// 最大勾选图例的数量
const MAX_LEGEND_SELECTED = 6;

export default function useChartInfoNew(props: {
  isLoading?: boolean;
  // 维度，即图例的keys
  dimensions: string[];
  // 游戏的数量
  gamesLength: number;
  // 指标的数量
  metricsLength: number;
}) {
  const { isLoading, dimensions, gamesLength, metricsLength } = props || {};

  const chartComp = useRef<IDBIBaseLineChartInstance>(null);
  const chartLegendSelected = useRef<{ [key: string]: boolean }>({});

  // echarts配色，二维数据，根据指标颜色 同一个颜色 由深到浅，根据游戏是不同的颜色
  const [chartColors, setChartColors] = useState<string[]>([...FLATTEN_ECHART_COLORS]);
  // echarts组件容器节点高度
  const [chartNodeHeight, setChartNodeHeight] = useState(300);
  // echarts组件图例的宽度
  const [chartLegendWidth, setChartLegendWidth] = useState(0);
  // echarts组件图例配置，多行
  const [chartLegendOptions, setChartLegendOptions] = useState<ILegendComponentOption[]>([]);

  const updateChartLegendWidth = () => {
    try {
      const chartNodeTemp = chartComp?.current?.getChartNode();
      if (!chartNodeTemp) return;
      const style = window.getComputedStyle(chartNodeTemp, null);
      const widthStyle = style.getPropertyValue('width');
      const width = Number.parseFloat(widthStyle);
      setChartLegendWidth(width);
    } catch (err) {
      console.error(err);
    }
  };

  const { run: updateChartLegendWidthRun, flush: updateChartLegendWidthFlush } = useThrottleFn(
    updateChartLegendWidth,
    {
      wait: 600,
      leading: true,
      trailing: true,
    },
  );

  const updateLegendInfoBySingleRow = (cfg: {
    chartLegendWidth: number;
    dimensions: string[];
    metricsLength: number;
  }) => {
    const { chartLegendWidth, dimensions } = cfg;
    // 是否只有一个指标
    const isOnlyOneMetric = metricsLength === 1;
    // 单行图例配色方案
    const colorsTemp: string[] = [];
    // 单行图例，所以直接 定义图例行时，固定写死一个数组元素，后面遍历再补充该行图例配置的data、selected
    const legendOptionsTemp: ILegendComponentOption[] = [
      {
        type: 'scroll',
        top: ECHART_LEGEND_TOP,
        // 图例宽度 为 容器宽度，左右留白各40px
        width: chartLegendWidth - ECHART_LEGEND_HORIZONTAL_BOUNDARY_WIDTH * 2,
        height: ECHART_LEGEND_HEIGHT,
        // 只有一行时，top直接就是grid的top + grid的height + 第一行图例跟grid的间隔距离
        data: [],
        selected: {},
      },
    ];
    // 单行图例 图例选中状态集合
    const legendSelectedTemp: { [key: string]: boolean } = {};
    // 遍历单行图例，追加单行图例的data、selected、配色
    for (let i = 0; i < dimensions?.length; i++) {
      const dimensionTemp = dimensions[i];
      legendOptionsTemp?.[0].data?.push(dimensionTemp);
      // 单行图例，默认选中前MAX_LEGEND_SELECTED个(6个)
      legendSelectedTemp[dimensionTemp] = i < MAX_LEGEND_SELECTED;

      /** 生成当前指标的配色, ECHART_COLORS是二维数组，保存的颜色，每一行都是不同的颜色，每一列是同一个颜色由深到浅的变化，不同游戏颜色不同，相同游戏的不同指标 体现为 同一颜色的由深到浅变化 **/
      /**
       * 当前指标配色行索引
       * 单指标 固定用第二行颜色
       * 多指标 从ECHART_COLORS，根据指标顺序 取出 每一行颜色，由于dimensions是按照指标排序的，所以相同的指标是连续的，所以 Math.floor(i / gamesLength) 就是指标的顺序索引
       */
      const colorRowIndex = isOnlyOneMetric ? 1 : Math.floor(i / gamesLength);
      /**
       * 当前指标配色列索引
       */
      const colorCellIndex = i % gamesLength;
      const color = ECHART_COLORS?.[colorRowIndex]?.[colorCellIndex];
      colorsTemp.push(color);
      /** 生成当前指标的配色 **/
    }

    legendOptionsTemp[0].selected = legendSelectedTemp;

    // chartLegendSelected保存 初始化的图例选中集合，在图例change事件内 需要用到
    chartLegendSelected.current = legendSelectedTemp;
    setChartColors(colorsTemp);
    setChartNodeHeight(ECHART_NODE_HEIGHT);
    setChartLegendOptions(legendOptionsTemp);
    // 需要让echart组件重新绘制，因为改变了echarts组件容器节点高度，echarts默认不会自动重新绘制，就会导致图形下方留白很多
    setTimeout(() => {
      chartComp?.current?.getChartInstance?.()?.resize();
    });
  };

  const updateLegendInfoByDoubleRow = (cfg: {
    chartLegendWidth: number;
    dimensions: string[];
    gamesLength: number;
    metricsLength: number;
  }) => {
    const { chartLegendWidth, dimensions } = cfg;
    // 图例配色方案
    const colorsTemp: string[] = [];
    // 图例，多行
    const legendOptionsTemp: ILegendComponentOption[] = [];
    // 图例 默认选中状态
    const legendSelectedTemp: { [key: string]: boolean } = {};
    // 图例总行数
    const totalRows = 2;
    // 最后一行图例的top值
    let topOfLastLegendRow = 0;
    // 当前行索引
    let rowIndex = 0;

    for (let i = 0; i < dimensions?.length; i++) {
      const dimensionTemp = dimensions[i];
      // 第一行，或者新的一行 图例
      if (i % gamesLength === 0) {
        // 计算当前行图例的top值
        const top = calcLegendTopOfMultiLegendMode({
          index: rowIndex,
          itemHeight: ECHART_LEGEND_HEIGHT,
          itemSpacing: ECHART_LEGEND_LINE_SPACING,
        });
        legendOptionsTemp.push({
          type: 'scroll',
          top,
          // 图例宽度 为 容器宽度，左右留白各40px
          width: chartLegendWidth - ECHART_LEGEND_HORIZONTAL_BOUNDARY_WIDTH * 2,
          height: ECHART_LEGEND_HEIGHT,
          data: [],
          selected: legendSelectedTemp,
        });
        // 最后一行图例时，保存最后一行图例的top值
        if (rowIndex === totalRows - 1) {
          topOfLastLegendRow = top;
        }
        rowIndex += 1;
      }
      // 更新当前图例 到 当前行图例配置中
      legendOptionsTemp[legendOptionsTemp.length - 1].data?.push(dimensionTemp);

      // 直接选中前6个图例
      legendSelectedTemp[dimensionTemp] = i < MAX_LEGEND_SELECTED;

      // 计算当前行图例的配色方案
      const rowColorsIndex = Math.floor(i / gamesLength);
      // 计算当前图例的配色方案
      const colorIndex = i % gamesLength;
      const rowColors = ECHART_COLORS?.[rowColorsIndex];
      colorsTemp.push(rowColors?.[colorIndex]);
    }

    // chartLegendSelected保存 初始化的图例选中集合，在图例change事件内 需要用到
    chartLegendSelected.current = legendSelectedTemp;
    setChartColors(colorsTemp);
    setChartNodeHeight(topOfLastLegendRow + ECHART_LEGEND_HEIGHT + ECHART_BOTTOM);
    setChartLegendOptions(legendOptionsTemp);
    // 需要让echart组件重新绘制，因为改变了echarts组件容器节点高度，echarts默认不会自动重新绘制，就会导致图形挤压
    setTimeout(() => {
      chartComp?.current?.getChartInstance?.()?.resize();
    });
  };

  useEffect(() => {
    if (isLoading) return;
    if (!chartLegendWidth) return;
    if (
      !dimensions?.length ||
      typeof gamesLength !== 'number' ||
      Number.isNaN(gamesLength) ||
      typeof metricsLength !== 'number' ||
      Number.isNaN(metricsLength)
    ) {
      setChartLegendOptions([]);
      return;
    }

    if (metricsLength === 2 && gamesLength > 1) {
      updateLegendInfoByDoubleRow({ chartLegendWidth, dimensions, gamesLength, metricsLength });
    } else {
      updateLegendInfoBySingleRow({ chartLegendWidth, dimensions, metricsLength });
    }
  }, [isLoading, chartLegendWidth, dimensions?.length, gamesLength, metricsLength]);

  // 处理图例选中事件，超过最大图例选中数量时，丢弃change事件回传的选中值
  useEffect(() => {
    if (isLoading) return;
    const chartInstance = chartComp?.current?.getChartInstance();
    if (!chartInstance) return;

    chartInstance?.on('legendselectchanged', (payload: any) => {
      let legendSelectedTemp = payload?.selected;
      const selectedCount = Object.values(legendSelectedTemp).filter((item) => item).length;
      legendSelectedTemp =
        selectedCount > MAX_LEGEND_SELECTED
          ? { ...chartLegendSelected.current }
          : { ...legendSelectedTemp };
      chartLegendSelected.current = legendSelectedTemp;
      const chartLegendOptionsTemp = chartLegendOptions?.map((chartLegendOption) => ({
        ...chartLegendOption,
        selected: legendSelectedTemp,
      }));
      setChartLegendOptions(chartLegendOptionsTemp);
    });

    return () => {
      chartInstance?.off('legendselectchanged');
    };
  }, [isLoading, chartLegendOptions]);

  useEffect(() => {
    if (isLoading) return;
    updateChartLegendWidthRun();
    updateChartLegendWidthFlush();
  }, [isLoading]);

  useEffect(() => {
    window.addEventListener('resize', updateChartLegendWidthRun, false);

    return () => {
      window.removeEventListener('resize', updateChartLegendWidthRun, false);
    };
  }, []);

  return {
    chartComp,
    chartNodeHeight,
    chartColors,
    chartLegendOptions,
  };
}
